import { useState, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Grid from '@mui/material/Grid2';

type PickerProps = {
	open?: boolean;
	onAccept: (date: Date | null, index: number) => void;
	closePicker: () => void;
	onConfirm: () => void;
	dates: (Date | null)[];
	reqId: number;
	isUpdateAssessmentDatesLoading: boolean;
};

export const useBulkRequestAssessmentDatePicker = () => {
	const [assessmentDates, setAssessmentDates] = useState<PickerProps['dates']>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [reqId, setReqId] = useState(0);

	const onAccept = useCallback((date: Date, index: number) => {
		setAssessmentDates((prev) => {
			prev[index] = date;
			return prev;
		});
	}, []);

	const openPicker = useCallback(() => {
		setIsOpen(true);
	}, []);

	const closePicker = useCallback(() => {
		setIsOpen(false);
	}, []);

	return {
		AssessmentDatePicker,
		openPicker,
		setAssessmentDates,
		setReqId,
		assessmentDates,
		pickerProps: {
			onAccept,
			open: isOpen,
			dates: assessmentDates,
			closePicker,
			reqId,
		},
	};
};

const AssessmentDatePicker = ({ dates, ...rest }: PickerProps) => {
	const { open = false, onAccept, onConfirm, closePicker, reqId, isUpdateAssessmentDatesLoading } = rest;

	const theme = useTheme();

	return (
		<Dialog open={open} fullWidth={true} maxWidth={'md'}>
			<DialogTitle>{`Choose your assessment dates for request ${reqId}`}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
					<Grid size={{ md: 4 }}>
						<DateTimePicker
							value={dates[0] ?? null}
							onAccept={(val) => onAccept(val, 0)}
							ampm
							slotProps={{
								actionBar: {
									actions: ['accept', 'clear'],
								},
							}}
							format={'dd MMM yyyy p'}
						/>
					</Grid>
					<Grid size={{ md: 4 }}>
						<DateTimePicker
							value={dates[1] ?? null}
							onAccept={(val) => onAccept(val, 1)}
							ampm
							slotProps={{
								actionBar: {
									actions: ['accept', 'clear'],
								},
							}}
							format={'dd MMM yyyy p'}
						/>
					</Grid>
					<Grid size={{ md: 4 }}>
						<DateTimePicker
							value={dates[2] ?? null}
							onAccept={(val) => onAccept(val, 2)}
							ampm
							slotProps={{
								actionBar: {
									actions: ['accept', 'clear'],
								},
							}}
							format={'dd MMM yyyy p'}
						/>
					</Grid>
					<Grid>
						<Typography variant="subtitle2" sx={{ color: theme.palette.grey[600] }}>
							By confirming the assessmesnt date/time, a confirmation email will be sent to the candidate. Please double
							check the date/time selected.
						</Typography>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button loading={isUpdateAssessmentDatesLoading} variant="outlined" color="primary" onClick={onConfirm}>
					<span>Confirm</span>
				</Button>
				<Button onClick={closePicker} autoFocus color="error" variant="outlined">
					Cancel
				</Button>
			</DialogActions>
		</Dialog>
	);
};
